import { useMemo, useState, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Copy, FileCode, LayoutTemplate, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/shared/components/page-header';
import { EmptyState } from '@/shared/components/empty-state';
import { ApiError, http } from '@/shared/api/client';
import { formatDate } from '@/shared/lib/format';
import { usePassbook } from '@/shared/lib/passbook';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { SegmentedControl } from '@/shared/ui/segmented-control';
import { Textarea } from '@/shared/ui/textarea';
import {
  BEEFREE_IMPORTER_NOT_CONFIGURED,
  BEEFREE_IMPORTER_NOT_CONFIGURED_MESSAGE,
  type BeefreeDocument,
} from '../model/beefree-document';
import { useEmailMarketing } from '../model/context';
import { useEmailMarketingPaths } from '../model/paths';
import { EDITOR_META } from '../model/template';
import { EmailPreview } from './email-preview';
import { ChannelErrorState, ChannelLoading, marketingErrorToast } from '@/features/marketing-shared';

const CATEGORIES = ['Todas', 'Promoção', 'Relacionamento', 'Aviso'] as const;
type CategoryFilter = (typeof CATEGORIES)[number];
/** Plataforma: padrão (todas as lojas) ou variante só de algumas. */
type ApplyFilter = 'all' | 'default' | 'stores';
const APPLY_OPTIONS: Array<{ value: ApplyFilter; label: string }> = [
  { value: 'all', label: 'Padrão e por loja' },
  { value: 'default', label: 'Padrão' },
  { value: 'stores', label: 'Por loja' },
];

function appliesLabel(template: { appliesTo?: { name: string }[] }): string {
  const count = template.appliesTo?.length ?? 0;
  return count ? `${count} loja${count > 1 ? 's' : ''}` : 'Padrão';
}

function appliesDetail(template: { appliesTo?: { name: string }[] }): string {
  const stores = template.appliesTo ?? [];
  return stores.length ? `vale para ${stores.map((store) => store.name).join(', ')}` : 'padrão para todas as lojas';
}

export function EmailTemplatesLegacyRedirect() {
  const { idPassbook } = usePassbook();
  const [params] = useSearchParams();
  const search = params.toString();
  return <Navigate to={`/loja/${idPassbook}/email-marketing/templates${search ? `?${search}` : ''}`} replace />;
}

/**
 * `embedded`: dentro de uma aba, sem o header da página. `frame`: a página
 * desenha o título e a navegação de quem a abriga (Plataforma), com as ações
 * da biblioteca na linha do título.
 */
export function EmailTemplatesPage({
  embedded = false,
  frame,
}: { embedded?: boolean; frame?: { title: string; nav: ReactNode } } = {}) {
  const navigate = useNavigate();
  const paths = useEmailMarketingPaths();
  const { templates, templatesState, duplicateTemplate, scope } = useEmailMarketing();
  // Template do sistema não vira campanha: a plataforma não tem público.
  const canUseInCampaign = scope.kind === 'passbook';
  const platform = scope.kind === 'platform';
  const [category, setCategory] = useState<CategoryFilter>('Todas');
  const [applyFilter, setApplyFilter] = useState<ApplyFilter>('all');
  const [query, setQuery] = useState('');
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importHtml, setImportHtml] = useState('');
  const [importError, setImportError] = useState('');
  const [importing, setImporting] = useState(false);
  const storeName = scope.storeName;

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return templates.filter((template) => {
      if (category !== 'Todas' && template.category !== category) return false;
      const stores = template.appliesTo?.length ?? 0;
      if (applyFilter === 'default' && stores > 0) return false;
      if (applyFilter === 'stores' && stores === 0) return false;
      if (!term) return true;
      return (
        template.name.toLowerCase().includes(term) ||
        template.description.toLowerCase().includes(term) ||
        template.tags.some((tag) => tag.toLowerCase().includes(term))
      );
    });
  }, [applyFilter, category, query, templates]);
  const preview = templates.find((template) => template.id === previewId) ?? null;

  const [duplicating, setDuplicating] = useState<string | null>(null);
  const duplicate = async (id: string) => {
    setDuplicating(id);
    try {
      const copy = await duplicateTemplate(id);
      toast.success('Modelo duplicado.', {
        description: canUseInCampaign ? 'A cópia já está na biblioteca da loja.' : 'A cópia fica nesta sessão.',
      });
      setPreviewId(copy.id);
    } catch (error) {
      const { message, description } = marketingErrorToast(error, 'Não foi possível duplicar o modelo.');
      toast.error(message, { description });
    } finally {
      setDuplicating(null);
    }
  };

  const createBlank = () => navigate(paths.templateCreate, { state: { blank: Date.now() } });

  const importHtmlTemplate = async () => {
    const html = importHtml.trim();
    if (!html) {
      setImportError('Cole o HTML do e-mail.');
      return;
    }
    setImporting(true);
    setImportError('');
    try {
      const data = await http.post<{ document: BeefreeDocument }>('/bff/email/beefree-import', { html });
      if (!data.document?.page) {
        setImportError('O Beefree não devolveu um documento. Nada foi aberto no editor.');
        return;
      }
      setImportOpen(false);
      setImportHtml('');
      navigate(paths.templateCreate, {
        state: { beefreeJson: JSON.stringify(data.document), html },
      });
    } catch (error) {
      const code =
        error instanceof ApiError && error.data && typeof error.data === 'object'
          ? (error.data as { code?: string }).code
          : undefined;
      if (code === BEEFREE_IMPORTER_NOT_CONFIGURED) {
        setImportError(error instanceof ApiError ? error.message : BEEFREE_IMPORTER_NOT_CONFIGURED_MESSAGE);
        return;
      }
      setImportError(error instanceof ApiError ? error.message : 'Não foi possível importar o HTML.');
    } finally {
      setImporting(false);
    }
  };

  const actions = (
    <>
      <Button type="button" variant="secondary" aria-label="Importar HTML" onClick={() => setImportOpen(true)}>
        <FileCode />
        <span className="hidden sm:inline">Importar HTML</span>
        <span className="sm:hidden">HTML</span>
      </Button>
      <Button type="button" aria-label="Criar em branco" onClick={createBlank}>
        <Plus />
        <span className="hidden sm:inline">Criar em branco</span>
        <span className="sm:hidden">Em branco</span>
      </Button>
    </>
  );
  const header = frame ? (
    <div className="mb-4 space-y-4">
      <PageHeader title={frame.title} actions={actions} />
      {frame.nav}
    </div>
  ) : (
    <PageHeader title="Templates" className={embedded ? 'mb-3 [&_h1]:sr-only' : undefined} actions={actions} />
  );

  if (templatesState.phase === 'loading') {
    return (
      <>
        {header}
        <ChannelLoading label="Carregando modelos" />
      </>
    );
  }

  if (templatesState.phase === 'error') {
    return (
      <>
        {header}
        <ChannelErrorState
          title="Não foi possível carregar os modelos"
          error={templatesState.error}
          onRetry={templatesState.refetch}
        />
      </>
    );
  }

  return (
    <div className="min-w-0">
      {header}
      {templates.length === 0 ? (
        <Card>
          <EmptyState
            icon={LayoutTemplate}
            title="Nenhum modelo"
            description="Crie um modelo em branco ou importe um HTML para começar a biblioteca."
            action={
              <Button type="button" onClick={createBlank}>
                <Plus /> Criar em branco
              </Button>
            }
          />
        </Card>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <SegmentedControl
              size="sm"
              value={category}
              onChange={setCategory}
              options={CATEGORIES.map((item) => ({ value: item, label: item }))}
            />
            {platform ? <SegmentedControl size="sm" value={applyFilter} onChange={setApplyFilter} options={APPLY_OPTIONS} /> : null}
            <div className="w-full sm:ml-auto sm:w-64">
              <Label htmlFor="busca-template" className="sr-only">
                Buscar modelo
              </Label>
              <Input
                id="busca-template"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Buscar modelo"
              />
            </div>
          </div>
          {filtered.length === 0 ? (
            <Card className="p-6 text-center text-sm text-muted-foreground">Nenhum modelo nessa categoria.</Card>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {filtered.map((template) => (
                <Card
                  key={template.id}
                  tabIndex={0}
                  aria-label={`Ver prévia de ${template.name}`}
                  onClick={() => setPreviewId(template.id)}
                  onKeyDown={(event) => {
                    if (event.target !== event.currentTarget) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setPreviewId(template.id);
                    }
                  }}
                  className="flex min-w-0 cursor-pointer flex-col p-3 outline-none transition-[border-color,background-color] duration-200 hover:border-primary/40 hover:bg-primary/[0.06] focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <div className="max-h-64 overflow-hidden rounded-xl">
                    <EmailPreview
                      variant="card"
                      subject={template.subject}
                      previewText={template.previewText}
                      blocks={template.blocks}
                      storeName={storeName}
                      editor={template.editor}
                      html={template.html}
                      fromName={scope.sender?.name}
                      fromEmail={scope.sender?.email ?? `novidades@${template.domain}`}
                      samples={scope.sampleVariables}
                    />
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span>{template.category}</span>
                    {platform ? <Badge variant={template.appliesTo?.length ? 'info' : 'secondary'}>{appliesLabel(template)}</Badge> : null}
                  </div>
                  <h2 className="mt-1 min-w-0 break-words font-medium">{template.name}</h2>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <Badge variant="outline">{EDITOR_META[template.editor].label}</Badge>
                    <Badge variant="outline">{template.origin === 'sandbox' ? 'Sandbox' : canUseInCampaign ? 'Da loja' : 'Nesta sessão'}</Badge>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">Atualizado em {formatDate(template.updatedAt)}</p>
                  <div
                    className="mt-4 flex flex-wrap gap-2"
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    <Button type="button" variant="secondary" size="sm" onClick={() => navigate(paths.templateEdit(template.id))}>
                      Editar
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => void duplicate(template.id)}
                      loading={duplicating === template.id}
                    >
                      <Copy /> Duplicar
                    </Button>
                    {canUseInCampaign ? (
                      <Button type="button" size="sm" onClick={() => navigate(paths.createWithTemplate(template.id))}>
                        Usar na campanha
                      </Button>
                    ) : null}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      <Dialog open={Boolean(preview)} onOpenChange={(open) => !open && setPreviewId(null)}>
        <DialogContent size="wide">
          {preview ? (
            <>
              <DialogHeader>
                <DialogTitle>{preview.name}</DialogTitle>
                <DialogDescription>
                  {preview.category} · {EDITOR_META[preview.editor].label}
                  {platform ? ` · ${appliesDetail(preview)}` : ''} · prévia com dados de exemplo
                </DialogDescription>
              </DialogHeader>
              <EmailPreview
                subject={preview.subject}
                previewText={preview.previewText}
                blocks={preview.blocks}
                storeName={storeName}
                editor={preview.editor}
                html={preview.html}
                fromName={scope.sender?.name}
                fromEmail={scope.sender?.email ?? `novidades@${preview.domain}`}
                samples={scope.sampleVariables}
              />
              <DialogFooter>
                <Button type="button" variant="secondary" onClick={() => void duplicate(preview.id)} loading={duplicating === preview.id}>
                  <Copy /> Duplicar
                </Button>
                <Button type="button" asChild>
                  <Link to={paths.templateEdit(preview.id)}>Editar</Link>
                </Button>
                {canUseInCampaign ? (
                  <Button type="button" onClick={() => navigate(paths.createWithTemplate(preview.id))}>
                    Usar na campanha
                  </Button>
                ) : null}
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={importOpen}
        onOpenChange={(open) => {
          setImportOpen(open);
          if (!open) setImportError('');
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Importar HTML</DialogTitle>
            <DialogDescription>
              O HTML vira um documento do Beefree e abre no editor. Salve os dados do e-mail para ele entrar na biblioteca.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="importar-html">HTML</Label>
            <Textarea
              id="importar-html"
              className="mt-1.5 font-mono"
              rows={8}
              value={importHtml}
              onChange={(event) => setImportHtml(event.target.value)}
            />
            {importError ? <p className="mt-2 text-sm text-destructive">{importError}</p> : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setImportOpen(false)}>
              Cancelar
            </Button>
            <Button type="button" onClick={() => void importHtmlTemplate()} loading={importing}>
              Abrir no editor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
